<script setup lang="ts">
import type { ButtonProps } from '#ui/types';

type BlockHeroLinedUpMedia =
    | {
          type: 'image';
          src: string;
      }
    | {
          type: 'YouTubeVideo';
          id: string;
          title: string;
      };

// PROPS
defineProps({
    title: { type: String, default: '' },
    description: { type: String, default: '' },
    media: { type: Object as PropType<BlockHeroLinedUpMedia>, required: true },
    button: { type: Object as PropType<Omit<ButtonProps, 'block' | 'color' | 'size'>>, default: undefined },
});

// EMITS
defineEmits(['cta-click']);
</script>

<template>
    <div class="space-y-14 lg:space-y-12">
        <BaseWidthContainer v-if="$slots.title || !!title" as="h1" class="text-primary-800 text-center text-3xl font-semibold">
            <slot name="title">{{ title }}</slot>
        </BaseWidthContainer>

        <img v-if="media.type === 'image'" :src="media.src" class="h-[400px] w-full object-cover" alt="" role="presentation" />

        <div v-else-if="media.type === 'YouTubeVideo'" class="mx-auto aspect-video w-full max-w-[1024px]">
            <iframe
                :src="`https://www.youtube-nocookie.com/embed/${media.id}`"
                :title="media.title"
                class="h-full w-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen
                loading="lazy"
                referrerpolicy="strict-origin-when-cross-origin"
            />
        </div>

        <BaseWidthContainer class="flex flex-col items-center gap-y-14 lg:gap-y-12">
            <div v-if="$slots.description || !!description" class="text-base">
                <slot name="description">{{ description }}</slot>
            </div>
            <UButton v-if="button" color="primary" size="xl" v-bind="button" @click="$emit('cta-click')" />
        </BaseWidthContainer>
    </div>
</template>
